import { Platform } from 'react-native';
import { Box, Button, Image, Text } from '@betty/moonlight';
import { memo, useCallback, useMemo, useState } from 'react';
import { ShopItemType } from '../../state/items/items.types';
import { useTranslation } from '@betty/i18n';
import ShopItemPrice from '../ShopItemPrice/ShopItemPrice';
import ShopBadgeList from '../badges/ShopBadgeList/ShopBadgeList';
import ShopItemInfo from '../ShopItemInfo/ShopItemInfo';
import { useDispatch } from '@betty/toolkit/state';
import { openShopRewardsBottomSheet, purchaseShopItem } from '../../state';
import { isAfterNow } from '@betty/toolkit/utils';
import { usePurchaseEligibilityCheck } from '../../hooks';
import { StackNavigationProp, useNavigation } from '@betty/navigation';

function ShopItem(item: ShopItemType) {
  const { id, sale, name, thumbnail, availableFrom, maxWidth, type } = item;
  const [t] = useTranslation('shop');
  const dispatch = useDispatch();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const isComingSoon = useMemo(
    () => (availableFrom ? isAfterNow(availableFrom) : false),
    [availableFrom],
  );
  const openErrorBottomSheet = usePurchaseEligibilityCheck();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();

  const isSaleItemSoldOut =
    sale?.availableItems !== undefined && sale?.availableItems === 0;

  const itemOpacity = isSaleItemSoldOut ? 0.3 : 1;

  const onRedeem = useCallback(async () => {
    if (openErrorBottomSheet) {
      return openErrorBottomSheet();
    }

    setIsPurchasing(true);
    const response = await dispatch(purchaseShopItem(id));

    if (purchaseShopItem.fulfilled.match(response)) {
      const validResponse = response.payload;
      const isWeb = Platform.OS === 'web';

      if (type === 'MysteryBox') {
        navigation.navigate(
          'ShopMysteryBox',
          isWeb
            ? {
                options: {
                  onClosed: () => {
                    dispatch(openShopRewardsBottomSheet(validResponse));
                  },
                },
              }
            : {},
        );

        // Open the rewards bottom sheet after the mystery box modal is closed (focus is returned to the shop screen. RN only)
        const unsubscribe = navigation.addListener('focus', () => {
          dispatch(openShopRewardsBottomSheet(validResponse));
          unsubscribe();
        });
      } else if (type !== 'Physical') {
        // Physical item is not being used but we need to ignore it just in case otherwise it will throw an undefined error
        dispatch(openShopRewardsBottomSheet(validResponse));
      }
    }

    setIsPurchasing(false);
  }, [id, dispatch, openErrorBottomSheet, type, navigation]);

  return (
    <Box maxWidth={maxWidth} flex={1} justifyContent="space-between">
      <Box position="relative">
        <Image
          source={thumbnail}
          aspectRatio={1}
          borderRadius="radius-400"
          opacity={itemOpacity}
        />
        <ShopItemInfo {...item} />
        {sale ? <ShopBadgeList sale={sale} /> : null}
      </Box>
      <Box gap="space-200" paddingTop="space-200" opacity={itemOpacity}>
        <Text
          variant="body-s-semi"
          color="darkSurfacePrimary"
          numberOfLines={2}
        >
          {name}
        </Text>
        <ShopItemPrice {...item} />
        {sale && sale.displayAvailableItems && sale.availableItems ? (
          <Text variant="caption" color="darkSurfaceMuted">
            {t('onlyXLeft', { itemsLeft: sale.availableItems })}
          </Text>
        ) : null}
        <Button
          locNS="shop"
          locKey={isComingSoon ? 'comingSoonCta' : 'redeemCta'}
          onPress={onRedeem}
          loading={isPurchasing}
          disabled={isPurchasing || isComingSoon}
        />
      </Box>
    </Box>
  );
}

export default memo(ShopItem);
